// Parser helpers for compile-time `var(...)` expression tokens.
// Example: `var(--sizes[ind].name)` is normalized here.
import { stripWrappingQuotes } from './selector'

export type ParsedExpressionVarCall = {
  variableName: string
  accessKey: string | null
  objectKey: string | null
}

// Read one compile-time `var(...)` token from an expression string.
export function readVarCallToken(source: string, start: number): { value: string; end: number } | null {
  const parsed = parseExpressionVarCall(source, start)
  // Reject unsupported `var(...)` call shapes.
  if (!parsed) {
    return null
  } else {
    return {
      value: serializeExpressionVarCall(parsed.value),
      end: parsed.end,
    }
  }
}

// Parse one supported `var(...)` call and return its normalized payload.
export function parseExpressionVarCall(
  source: string,
  start: number,
): { value: ParsedExpressionVarCall; end: number } | null {
  // Reject positions that do not start with `var(`.
  if (source.slice(start, start + 4) !== 'var(') return null
  let cursor = skipWhitespace(source, start + 4)
  // Reject malformed calls without a custom property prefix.
  if (source[cursor] !== '-' || source[cursor + 1] !== '-') return null
  const nameStart = cursor
  cursor += 2
  // Reject malformed variable names without an identifier start.
  if (!isIdentifierStart(source[cursor] ?? '')) return null
  cursor += 1
  // Consume variable-name identifier chars.
  while (cursor < source.length && isCustomPropertyNameBody(source[cursor])) cursor += 1
  const value: ParsedExpressionVarCall = {
    variableName: source.slice(nameStart, cursor),
    accessKey: null,
    objectKey: null,
  }
  cursor = skipWhitespace(source, cursor)
  // Parse optional bracket access after the variable name.
  if (source[cursor] === '[') {
    const parsedAccess = readBracketAccessToken(source, cursor)
    if (!parsedAccess) return null
    value.accessKey = parsedAccess.value
    cursor = parsedAccess.end
  }
  cursor = skipWhitespace(source, cursor)
  // Parse optional direct object-key access.
  if (source[cursor] === '.') {
    const parsedObjectKey = readObjectKeyToken(source, cursor + 1)
    if (!parsedObjectKey) return null
    value.objectKey = parsedObjectKey.value
    cursor = parsedObjectKey.end
  }
  cursor = skipWhitespace(source, cursor)
  // Reject unterminated `var(...)` calls.
  if (source[cursor] !== ')') return null
  return { value, end: cursor + 1 }
}

// Serialize one parsed var-call back into a stable normalized token form.
function serializeExpressionVarCall(value: ParsedExpressionVarCall): string {
  const accessPart = value.accessKey === null ? '' : `[${value.accessKey}]`
  const keyPart = value.objectKey === null ? '' : `.${value.objectKey}`
  // Return the normalized var-call token text.
  return `var(${value.variableName}${accessPart}${keyPart})`
}

// Read one `[indexOrKey]` access token.
function readBracketAccessToken(source: string, start: number): { value: string; end: number } | null {
  let cursor = skipWhitespace(source, start + 1)
  // Reject empty bracket selectors.
  if (!isBracketAccessStart(source[cursor] ?? '')) return null
  const keyStart = cursor
  cursor += 1
  // Consume bracket selector chars until the closing bracket.
  while (cursor < source.length && isBracketAccessBody(source[cursor])) cursor += 1
  cursor = skipWhitespace(source, cursor)
  // Reject malformed bracket selectors without `]`.
  if (source[cursor] !== ']') return null
  return {
    value: source.slice(keyStart, cursor),
    end: cursor + 1,
  }
}

// Read one direct `.key` token after a variable or bracket access.
function readObjectKeyToken(source: string, start: number): { value: string; end: number } | null {
  // Reject invalid object-key starts.
  if (!isIdentifierStart(source[start] ?? '')) return null
  let end = start + 1
  // Consume identifier chars in the object-key segment.
  while (end < source.length && isIdentifierBody(source[end])) end += 1
  return { value: source.slice(start, end), end }
}

// Resolve one bracket selector against scope and literal fallback rules.
export function resolveBracketAccessKey(accessKey: string | null, scope: Record<string, string>): string | null {
  // Reject missing bracket selectors.
  if (accessKey === null) return null
  if (/^\d+$/.test(accessKey)) return accessKey
  if (accessKey in scope) return stripWrappingQuotes(scope[accessKey].trim())
  // Return literal object-key selector when scope has no override.
  return accessKey
}

// Skip parser whitespace inside a var-call token.
function skipWhitespace(source: string, start: number): number {
  let cursor = start
  // Advance past ASCII whitespace characters.
  while (source[cursor] === ' ' || source[cursor] === '\t' || source[cursor] === '\n' || source[cursor] === '\r' || source[cursor] === '\f') cursor += 1
  return cursor
}

// Check whether a char can start an identifier or numeric bracket selector.
function isBracketAccessStart(char: string): boolean {
  return isIdentifierStart(char) || isDigit(char)
}

// Check whether a char can continue a bracket selector token.
function isBracketAccessBody(char: string): boolean {
  return isIdentifierBody(char) || char === '-'
}

// Check whether a char can continue a custom-property name after `--`.
function isCustomPropertyNameBody(char: string): boolean {
  return isIdentifierBody(char) || char === '-'
}

// Check whether a char can start an identifier token.
function isIdentifierStart(char: string): boolean {
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '_'
}

// Check whether a char can continue an identifier token.
function isIdentifierBody(char: string): boolean {
  return isIdentifierStart(char) || isDigit(char)
}

// Check whether a char is a decimal digit.
function isDigit(char: string): boolean {
  return char >= '0' && char <= '9'
}
